import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useUpdateV1IncidentMutation } from 'hooks/v1Incident/useUpdateV1Incident';
import { IncidentGeoLocationPicker } from './IncidentGeoLocationPicker';
import { getLatLongFromGeometry } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/getLatLongFromGeometry';

const getLngLatDisplayValue = (geometry) => {
  const coordinates = getLatLongFromGeometry(geometry);
  if (!coordinates) return '';

  return `${coordinates.longitude}, ${coordinates.latitude}`;
};

export default function EditIncidentDialog({ show, incident, onClose }) {
  const [address, setAddress] = useState(incident?.address);
  const updateIncidentHook = useUpdateV1IncidentMutation();
  const [isLoaded, setIsLoaded] = useState(true);

  const areIncidentsLoaded = useSelector((state) => {
    return state.app.areIncidentsLoaded;
  });

  let initOperationalPeriodStartDateTime;
  let initOperationalPeriodInterval;

  if (!!incident.settings && !!incident.settings.operationalPeriod) {
    initOperationalPeriodStartDateTime =
      incident.settings.operationalPeriod.operationalPeriodStartDateTime;
    initOperationalPeriodInterval =
      incident.settings.operationalPeriod.operationalPeriodInterval;
  }

  const reduxCurrentlySelectedGroup = useSelector((state) => {
    return state.app.currentlySelectedGroup;
  });

  const reduxValidateRBACPermissionForActionResult = useSelector((state) => {
    return state.app.validateRBACPermissionForActionResult;
  });

  const [name, setName] = useState(incident.name);
  const [geometry, setGeometry] = useState(incident?.location);
  const [lngLatInput, setLngLatInput] = useState(() =>
    getLngLatDisplayValue(incident?.location)
  );
  const nameError = name === '';

  const operationalPeriodStartDateTime = initOperationalPeriodStartDateTime;
  const operationalPeriodInterval = initOperationalPeriodInterval;

  useEffect(() => {
    if (!isLoaded && areIncidentsLoaded) {
      onClose();
    }
  }, [areIncidentsLoaded, isLoaded, onClose]);

  const onGeometryChange = useCallback((geometry) => {
    setAddress(null);
    setGeometry(geometry);

    setLngLatInput(getLngLatDisplayValue(geometry));
  }, []);

  const handleUpdateIncidentClick = async () => {
    setIsLoaded(false);

    let changedName = name;

    const updatedIncident = { ...incident };
    updatedIncident.name = changedName;
    updatedIncident.settings = incident.settings || {};
    updatedIncident.type = incident.type;

    if (
      operationalPeriodStartDateTime &&
      operationalPeriodStartDateTime &&
      !isNaN(parseInt(operationalPeriodInterval)) &&
      !(!!incident.settings && !!incident.settings.operationalPeriod)
    ) {
      updatedIncident.settings.operationalPeriod = {
        operationalPeriodStartDateTime:
          operationalPeriodStartDateTime.toISOString(),
        operationalPeriodInterval: operationalPeriodInterval,
      };
    }

    await updateIncidentHook.mutateAsync({
      v1IncidentId: incident.id,
      name: updatedIncident.name,
      settings: updatedIncident.settings,
      archived: updatedIncident.archived,
      incidentTypes: updatedIncident.incident_types ?? [],
      location: geometry ? geometry : null,
      address,
    });
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>Edit Incident Name</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">Group</label>
          <StylishNewInput
            readOnly
            disabled
            value={reduxCurrentlySelectedGroup.group_name}
          />
        </div>
        <div className="m-0">
          <label className="form-label">Incident Name</label>
          <StylishNewInput
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError && (
            <span className="form-text form-error">This field is required</span>
          )}
        </div>
        <div className="mt-3">
          <div className="mb-2">
            <label className="form-label">Address</label>
            <StylishNewInput
              value={address ?? ''}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Lng/Lat</label>
            <StylishNewInput readOnly value={lngLatInput} />
          </div>
          <label className="form-label">Location</label>
          <IncidentGeoLocationPicker
            geometry={geometry}
            address={address}
            onGeometryChange={onGeometryChange}
            onAddressChange={setAddress}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="button-group">
          <StylishNewButton
            className="button--secondary button--reverse"
            type="button"
            onClick={onClose}
          >
            Close
          </StylishNewButton>
          <StylishNewButton
            disabled={!!nameError}
            onClick={() => handleUpdateIncidentClick()}
            className="button--primary"
          >
            Save Incident
          </StylishNewButton>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
